import { Tag, ViewEditNote } from "@landvoice/icons";
import { ListingStats } from "./ListingStats";
import { HeartIcon } from "src/components/neighborhoodSearch/components/HeartIcon";
import { Link } from "react-router-dom";
import DistressRate from "./distressRate";
import StatusTag from "./StatusTag";
import OwnerContactInfo from "./OwnerContactInfo";
import { timeAgo } from "./timeAgo";
import formatPhoneNumber from "./formatPhones";
import removeParamFromString from "./removeParamsFromString";
import { EnumProductTypes } from "../../types/listing";
import HorizontalDivider from "./HorizontalDivider";
import { ReactNode, useState } from "react";
import { Listing } from "src/types/listing";
import { PropertyActionsToolbar } from "./PropertyActionsToolbar";
import { FiCopy } from "react-icons/fi";
import { FaCheck } from "react-icons/fa6";
import copyTextToClipboard from "./copyToCLipBoard";


const InfoIcon = ({
  icon,
  text,
  textSize = "12px",
}: {
  icon: ReactNode;
  text: string;
  textSize?: string;
  gap?: string;
}) => {
  return (
    <div className={` flex min-w-[54px] gap-[4px] text-gray-primary`}>
      {icon}
      <p className={`text-[${textSize}]`}>{text}</p>
    </div>
  );
};

const Divider = ({ height = "h-4" }) => {
  return <div className={`w-[1px] ${height} bg-gray-300`} />;
};

const HDivider = () => {
  return <HorizontalDivider className="max-w-[800px]" />;
};

const InfoText = ({
  title,
  info,
  color = "text-gray-primary",
}: {
  title: string;
  info: string | number | null | undefined;
  color?: string;
}) => {
  return (
    <div className="h-[46px] min-w-[30px]  ">
      <p className="text-[12px] text-gray-secondary whitespace-nowrap">{title}</p>
      <p className={`text-[16px]  leading-6 mt-2 whitespace-nowrap ${color}`}>
        {info}
      </p>
    </div>
  );
};

const InfoIconButton = ({
  title,
  icon,
  buttonText,
  func,
  edit = false,
}: {
  title: string;
  icon: ReactNode;
  buttonText: string;
  func: () => void;
  edit?: boolean;
}) => {
  return (
    <div className=" h-[46px] min-w-[10px] cursor-pointer" onClick={func}>
      <p className="text-[12px] text-gray-secondary">{title}</p>
      <div className={edit ? " editInfoButton" : "addInfoButton"}>
        {icon}
        <p className="text-[11px] font-bold text-center mx-auto leading-4 whitespace-nowrap">
          {buttonText}
        </p>
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StatusInfo = ({ listing }: { listing: Listing }) => {
  const { mlsNumber, distressedLevel, productType, status, statusDate } =
    listing;
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyMlsId = async () => {
    if (!mlsNumber) return;
    try {
      await copyTextToClipboard(mlsNumber);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy MLS ID: ", err);
    }
  };

  return productType === EnumProductTypes.distressed && !status ? (
    <DistressRate rate={distressedLevel} />
  ) : (
    <div className="flex gap-5 text-gray-secondary  pr-3 items-baseline">
      <StatusTag status={status || "Unknown"} date={statusDate || ""} />
      {mlsNumber && (
        <div className="flex items-center gap-1">
          <p className="text-[14px]">MLS ID: {mlsNumber}</p>
          <button
            type="button"
            onClick={handleCopyMlsId}
            className="text-gray-secondary hover:text-gray-primary transition-colors p-1"
            title="Copy MLS ID"
          >
            {isCopied ? (
              <FaCheck className="text-success w-3 h-3" />
            ) : (
              <FiCopy className="w-3 h-3" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export type PropertySummaryProps = {
  listing: Listing;
  suppressPrice?: boolean;
  onOpenNotesClick?: () => void;
  onOpenTagsClick?: () => void;
  all?: boolean;
  onTitleClick?: () => void;
  onPrintClick?: () => void;
  onFavoriteClick?: () => void;
  isDetails?: boolean;
  isSuppressStatusInfo?: boolean;
  isSuppressTitle?: boolean;
  isTagsAndNotesEnabled?: boolean;
  sendCardsTemplateCategory?: string;
  sendCardCount?: number;
  toolbarPlacement?: 'status-bar' | 'title';
};

const PropertySummary = ({
  listing,
  suppressPrice = false,
  onOpenNotesClick,
  onOpenTagsClick,
  all = true,
  onTitleClick,
  onPrintClick,
  onFavoriteClick,
  isDetails = false,
  isTagsAndNotesEnabled = false,
  isSuppressStatusInfo = false,
  isSuppressTitle = false,
  sendCardsTemplateCategory = "",
  sendCardCount = 0,
  toolbarPlacement = 'status-bar',
}: PropertySummaryProps) => {

  const {
    title,
    formattedPrice = 0,
    dom = 0,
    notes = [],
    tags = [],
    status,
    publishDate,
    formattedPublishDate,
    formattedAuctionDate,
    auctionDate,
    hasDom = false,
    mapLink,
    isFavorite,
  } = listing;

  const titleSection = (
    <div className="flex mb-3 flex-wrap items-center gap-2">
      <div className="font-semibold gap-2 text-[20px] text-gray-primary flex flex-wrap items-center">
        <span
          className="hover:text-primary cursor-pointer leading-5"
          onClick={onTitleClick}
        >{`${title}`}</span>
        {onFavoriteClick && (
          <HeartIcon
            isFavorite={isFavorite}
            onClick={
              onFavoriteClick
                ? (e) => {
                    e.stopPropagation();
                    onFavoriteClick();
                  }
                : undefined
            }
          />
        )}
      </div>
      {toolbarPlacement === 'title' && (
        <div className="order-first w-full md:order-none md:w-auto">
          <PropertyActionsToolbar
            listing={listing}
            mapLink={mapLink}
            title={title}
            onPrintClick={onPrintClick}
            sendCardsTemplateCategory={sendCardsTemplateCategory}
            sendCardCount={sendCardCount}
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`md:min-w-[200px] lg:min-w-[550px] max-w-[90vw] break-inside-avoid pr-4 ${
        all ? "xl:min-h-[218px]" : ""
      }`}
    >
      {!isSuppressStatusInfo && toolbarPlacement === 'status-bar' && (
        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-2 md:mb-0">
          <StatusInfo listing={listing} />
          <PropertyActionsToolbar
            listing={listing}
            mapLink={mapLink}
            title={title}
            onPrintClick={onPrintClick}
            sendCardsTemplateCategory={sendCardsTemplateCategory}
            sendCardCount={sendCardCount}
          />
        </div>
      )}

      {!isSuppressTitle && (
        <div className="min-h-[42px] max-w-[90vw] mb-3 lg:mb-8">
          {mapLink && isDetails ? (
            <Link target={isDetails ? "_blank" : "_self"} to={mapLink}>
              {titleSection}
            </Link>
          ) : (
            titleSection
          )}
          <ListingStats
            listing={listing}
            show={["beds", "bath", "sqrFeet", "lotSize", "yearBuilt", "type"]}
          />
        </div>
      )}
      {all && (
        <div className="flex md:gap-[30px] gap-3  max-w-[90vw] flex-col sm:flex-row">
          {(!suppressPrice || hasDom || publishDate || auctionDate) && (
            <div className="flex md:gap-[30px] gap-3 ">
              {!suppressPrice && (
                <InfoText
                  title={
                    status === "Notice of Trustee Sale" ||
                    status === "Notice of Sale"
                      ? "Min Bid"
                      : formattedAuctionDate
                      ? "List Price"
                      : "Price"
                  }
                  info={formattedPrice || "--"}
                  color={"text-gray-primary"}
                />
              )}
              {hasDom && <InfoText title="DOM" info={dom} />}
              {publishDate && (
                <InfoText title="Publish Date" info={formattedPublishDate} />
              )}
              {auctionDate && (
                <InfoText title="Auction Date" info={formattedAuctionDate} />
              )}
            </div>
          )}

          {isTagsAndNotesEnabled && (
            <div className="flex md:gap-[30px] gap-3">
              <InfoIconButton
                title="Notes"
                icon={<ViewEditNote style={{ height: "8px" }} />}
                buttonText={
                  notes.length && notes[0]?.note && notes[0]?.note !== " "
                    ? `${notes[0]?.note.substring(0, 20)}${
                        notes[0]?.note.length > 20 ? "..." : ""
                      }`
                    : "Add Note"
                }
                func={() => {
                  onOpenNotesClick?.();
                }}
                edit={Boolean(
                  notes.length && notes[0]?.note && notes[0]?.note !== " "
                )}
              />
              <InfoIconButton
                title="Tags"
                icon={<Tag style={{ height: "8px" }} />}
                buttonText={
                  tags.length
                    ? tags.slice(0, 2).join(" ").substring(0, 20)
                    : "Add Tag"
                }
                func={() => {
                  onOpenTagsClick?.();
                }}
                edit={!!tags.length}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export {
  InfoIcon,
  Divider,
  InfoText,
  HDivider,
  OwnerContactInfo,
  formatPhoneNumber,
  timeAgo,
  removeParamFromString,
  DistressRate,
};

export default PropertySummary;
